import { useEffect, useState } from 'react';
import PerformanceTracker, {
  getPerformanceTracker,
  PerformanceMetrics,
} from '@/utils/performanceMetrics';
import { useVideoPool } from '@/utils/useVideoPool';
import styles from './PerformanceMonitor.module.css';

interface PerformanceMonitorProps {
  isVisible: boolean;
}

export const PerformanceMonitor = ({ isVisible }: PerformanceMonitorProps) => {
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const { pool } = useVideoPool();

  useEffect(() => {
    if (!isVisible) return;

    const tracker = getPerformanceTracker();

    const updateMetrics = () => {
      // Update video pool metrics
      const poolMetrics = pool.getMetrics();
      tracker.updateVideoMetrics({
        poolSize: poolMetrics.poolSize,
        inUse: poolMetrics.inUse,
        cacheHitRate: pool.getCacheHitRate(),
      });

      // Get all metrics
      setMetrics(tracker.getMetrics());
    };

    // Initial update
    updateMetrics();

    // Update every second
    const interval = setInterval(updateMetrics, 1000);

    return () => clearInterval(interval);
  }, [isVisible, pool]);

  if (!isVisible || !metrics) return null;

  const memoryUsagePercent =
    metrics.memory.limit > 0 ? (metrics.memory.used / metrics.memory.limit) * 100 : 0;

  if (isMinimized) {
    return (
      <div className={styles.minimized} onClick={() => setIsMinimized(false)}>
        <div className={styles.minimizedContent}>
          <span className={styles.minimizedIcon}>📊</span>
          <span className={styles.minimizedText}>Performance</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Performance Monitor</h3>
        <button className={styles.minimizeBtn} onClick={() => setIsMinimized(true)}>
          _
        </button>
      </div>

      <div className={styles.content}>
        {/* Memory Section */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Memory</h4>
          <div className={styles.metric}>
            <span className={styles.label}>Used:</span>
            <span className={styles.value}>
              {PerformanceTracker.formatBytes(metrics.memory.used)}
            </span>
          </div>
          <div className={styles.metric}>
            <span className={styles.label}>Limit:</span>
            <span className={styles.value}>
              {PerformanceTracker.formatBytes(metrics.memory.limit)}
            </span>
          </div>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{
                width: `${Math.min(memoryUsagePercent, 100)}%`,
                backgroundColor:
                  memoryUsagePercent > 80
                    ? '#ef4444'
                    : memoryUsagePercent > 60
                      ? '#f59e0b'
                      : '#10b981',
              }}
            />
          </div>
        </div>

        {/* Video Pool Section */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Video Pool</h4>
          <div className={styles.metric}>
            <span className={styles.label}>Pool Size:</span>
            <span className={styles.value}>{metrics.videos.poolSize}</span>
          </div>
          <div className={styles.metric}>
            <span className={styles.label}>In Use:</span>
            <span className={styles.value}>{metrics.videos.inUse}</span>
          </div>
          <div className={styles.metric}>
            <span className={styles.label}>Cache Hit Rate:</span>
            <span className={styles.value}>{(metrics.videos.cacheHitRate * 100).toFixed(1)}%</span>
          </div>
          <div className={styles.metric}>
            <span className={styles.label}>Total Loaded:</span>
            <span className={styles.value}>{metrics.videos.totalLoaded}</span>
          </div>
        </div>

        {/* Load Times Section */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Load Times</h4>
          <div className={styles.metric}>
            <span className={styles.label}>Last:</span>
            <span className={styles.value}>
              {PerformanceTracker.formatTime(metrics.loadTimes.last)}
            </span>
          </div>
          <div className={styles.metric}>
            <span className={styles.label}>Average:</span>
            <span className={styles.value}>
              {PerformanceTracker.formatTime(metrics.loadTimes.average)}
            </span>
          </div>
          <div className={styles.metric}>
            <span className={styles.label}>Samples:</span>
            <span className={styles.value}>{metrics.loadTimes.samples.length}</span>
          </div>
        </div>

        {/* Network Section */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Network</h4>
          <div className={styles.metric}>
            <span className={styles.label}>Bytes Loaded:</span>
            <span className={styles.value}>
              {PerformanceTracker.formatBytes(metrics.network.bytesLoaded)}
            </span>
          </div>
          <div className={styles.metric}>
            <span className={styles.label}>Active Requests:</span>
            <span className={styles.value}>{metrics.network.activeRequests}</span>
          </div>
        </div>

        {/* Mini Histogram for Load Times */}
        {metrics.loadTimes.samples.length > 0 && (
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Load Time Distribution</h4>
            <div className={styles.histogram}>
              {metrics.loadTimes.samples.slice(-20).map((time, i) => {
                const maxTime = Math.max(...metrics.loadTimes.samples);
                const height = (time / maxTime) * 100;
                return (
                  <div
                    key={i}
                    className={styles.histogramBar}
                    style={{ height: `${height}%` }}
                    title={PerformanceTracker.formatTime(time)}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
